import type React from "react";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";

import ChevronIcon from "@/assets/svg/chevron.svg?react";
import CloseIcon from "@/assets/svg/close-sm.svg?react";
import { H5, P } from "@/base/components/Global";
import Icon from "@/base/components/Global/Icon";
import GenericModal from "@/base/components/Modal/GenericModal";
import { APOLLO_CLUB_STAR_WHITE } from "@/constants/blobIcons";
import {
  fetchInvestorLoyaltyBenefit,
  selectInvestorLoyaltyBenefit,
} from "@/store/cms";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { hasData, shouldLoad } from "@/store/utils";
import { createUseStyles } from "@/theme";

const BANNER_BRAND_COLOR = "#7e07b0";

const useStyles = createUseStyles(({ spacing, font, borderRadius, color }) => ({
  container: {
    backgroundColor: BANNER_BRAND_COLOR,
    borderRadius: [0, 0, borderRadius.l, borderRadius.l],
    paddingLeft: spacing.l,
    paddingRight: spacing.l,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xxs,
    marginTop: -spacing.xxl,
    position: "relative",
    zIndex: 0,
    width: "100%",
  },
  content: {
    overflow: "hidden",
  },
  description: {
    fontWeight: font.weight.s,
    paddingTop: spacing.l,
    display: "flex",
    flexDirection: "column",
    gap: spacing.m,
    "& p": {
      color: color.white,
      marginBottom: "12px",
    },
  },
  tierRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing.m,
    minHeight: spacing.x4l - spacing.xs,
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    width: "100%",
  },
  tierIcon: {
    flexShrink: 0,
  },
  tierName: {
    flex: 1,
    fontSize: font.size.ms,
    color: color.white,
    textAlign: "left",
  },
  chevronButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    padding: spacing.m,
    cursor: "pointer",
    transition: "transform 0.3s ease",
    flexShrink: 0,
  },
  chevronExpanded: {
    transform: "rotate(180deg)",
  },
  chevronIcon: {
    display: "block",
    "& path": {
      stroke: color.white,
    },
  },
  termsButton: {
    background: "none",
    border: "none",
    padding: [spacing.xs, 0],
    marginTop: -spacing.m,
    color: color.white,
    fontSize: font.size.xxs,
    textDecoration: "underline",
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  termsModalContent: {
    position: "relative",
    "& p": {
      color: color.gray2,
    },
  },
  termsCloseButton: {
    position: "absolute",
    top: 0,
    right: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: spacing.xs,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const InvestorClubCardBanner: React.FC = () => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const investorBenefit = useAppSelector(selectInvestorLoyaltyBenefit);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (shouldLoad(investorBenefit)) {
      void dispatch(fetchInvestorLoyaltyBenefit());
    }
  }, [investorBenefit]);

  if (
    !hasData(investorBenefit) ||
    (investorBenefit.title === null && investorBenefit.description === null)
  ) {
    return null;
  }

  const { title, description, terms } = investorBenefit;
  const hasDescription = !!description;
  const hasTerms = !!terms;

  return (
    <div className={classes.container}>
      {hasDescription && (
        <motion.div
          className={classes.content}
          initial={false}
          animate={{
            height: isExpanded ? "auto" : 0,
            opacity: isExpanded ? 1 : 0,
          }}
          transition={{
            height: { type: "spring", stiffness: 240, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        >
          <div className={classes.description}>
            <P cmsContent={description} noMargin />
            {hasTerms && (
              <button
                type="button"
                className={classes.termsButton}
                onClick={() => setIsTermsOpen(true)}
              >
                {terms.triggerLabel}
              </button>
            )}
          </div>
        </motion.div>
      )}

      <button
        type="button"
        className={classes.tierRow}
        onClick={() => hasDescription && setIsExpanded(!isExpanded)}
      >
        <Icon
          icon={APOLLO_CLUB_STAR_WHITE}
          width={24}
          height={24}
          className={classes.tierIcon}
        />

        <span className={classes.tierName}>{title}</span>

        {hasDescription && (
          <span
            className={classNames(
              classes.chevronButton,
              isExpanded && classes.chevronExpanded,
            )}
          >
            <ChevronIcon className={classes.chevronIcon} />
          </span>
        )}
      </button>

      {hasTerms && (
        <GenericModal
          isOpen={isTermsOpen}
          onClose={() => setIsTermsOpen(false)}
        >
          <div className={classes.termsModalContent}>
            <button
              type="button"
              className={classes.termsCloseButton}
              onClick={() => setIsTermsOpen(false)}
            >
              <CloseIcon />
            </button>
            <H5 gray1 noMargin>
              {terms.title}
            </H5>
            <P cmsContent={terms.content} />
          </div>
        </GenericModal>
      )}
    </div>
  );
};

export default InvestorClubCardBanner;
